import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { ArrowPathIcon } from "@heroicons/react/24/outline";
import { XMarkIcon } from "@heroicons/react/24/solid";
import { handleSpaceKey } from "../app/keyboardUtils";
import useVersionCheck from "../app/useVersionCheck";

const DISMISSED_KEY = "spa-update-dismissed-commit";

// The build whose notice has been dismissed. Held in memory so that dismissing,
// and clearing that dismissal at sign in, behave the same either way, whether or
// not session storage will take a write. Storage only mirrors it, which buys one
// extra thing: a dismissal that survives a reload of the tab.
//
// Dismissal is per build and per tab, so a user who says "later" is not asked
// again for the same deploy.
let dismissedCommit: string | null = null;
let isHydrated = false;

function readDismissed() {
	if (!isHydrated) {
		isHydrated = true;
		try {
			dismissedCommit = sessionStorage.getItem(DISMISSED_KEY);
		} catch {
			// Storage can be unavailable in private windows. Memory alone covers
			// everything except surviving a reload, and a reload is what the
			// notice was asking for anyway.
		}
	}
	return dismissedCommit;
}

function writeDismissed(commitHash: string) {
	dismissedCommit = commitHash;
	isHydrated = true;
	try {
		sessionStorage.setItem(DISMISSED_KEY, commitHash);
	} catch {
		// See readDismissed.
	}
}

// Called from the shared sign-in bootstrap. A tab left running overnight can
// have a dismissed notice and a stale bundle by morning, and signing back in is
// the natural moment to raise it again.
export function clearDismissedUpdateNotice() {
	dismissedCommit = null;
	// Memory is authoritative from here, so a later read must not pull the value
	// this call was meant to get rid of back out of storage.
	isHydrated = true;
	try {
		sessionStorage.removeItem(DISMISSED_KEY);
	} catch {
		// See readDismissed.
	}
}

export default function UpdateAvailableBanner() {
	const location = useLocation();
	const deployed = useVersionCheck(location.pathname);
	const [dismissed, setDismissed] = useState(readDismissed);

	// Signing in clears the dismissal, and every sign-in path navigates on the
	// way out, so re-reading it here is what brings the notice back for someone
	// who dismissed it and then left the tab open overnight.
	useEffect(() => {
		setDismissed(readDismissed());
	}, [location.pathname]);

	if (!deployed || dismissed === deployed.commitHash) return null;

	function handleReload() {
		// A reload the user asked for revalidates the entry document even when
		// it is still in the browser cache, so this picks up the new build
		// whether or not the cache headers on index.html took effect.
		window.location.reload();
	}

	function handleDismiss() {
		if (!deployed) return;
		writeDismissed(deployed.commitHash);
		setDismissed(deployed.commitHash);
	}

	// z-[5] keeps this above ordinary page content but below everything that is
	// asking for a decision: ModalFrame's dialog is z-10 and the delete
	// confirmation in DeleteCourseButton is z-50, both rendered at body level
	// where nothing boxes them into their own stacking context. A higher value
	// floats this notice over an open modal, where Headless UI has already made
	// it inert, so it cannot even be dismissed there. Sitting underneath means a
	// modal backdrop just greys it out until the modal is closed.
	return (
		<section
			role="status"
			aria-live="polite"
			className="fixed bottom-4 right-4 z-[5] max-w-sm rounded border border-green-400 bg-green-100 text-green-800 shadow-lg">
			<div className="flex items-start gap-3 p-4">
				<ArrowPathIcon className="mt-0.5 h-6 w-6 shrink-0 text-green-600" aria-hidden="true" />
				<div className="grow">
					<p className="font-semibold">A new version is available</p>
					<p className="mt-1 text-sm">
						Reload when you reach a good stopping point to get the latest updates.
					</p>
					<div className="mt-3 flex items-center gap-3">
						{/*
							This banner is mounted outside SiteConfig, so it can be on
							screen before data-theme is set and the --primary-* variables
							behind btn-primary resolve. Its colors are fixed for the same
							reason AlertMessage's are.
						*/}
						<button
							type="button"
							className="btn btn-small bg-green-700 text-white hover:bg-green-800"
							onClick={handleReload}>
							Reload now
						</button>
						<button type="button" className="text-sm underline" onClick={handleDismiss}>
							Later
						</button>
					</div>
				</div>
				<button
					aria-label="Dismiss the new version notice"
					className="h-6 w-6 shrink-0 text-gray-700"
					onClick={handleDismiss}
					onKeyDown={(e) => handleSpaceKey(e, handleDismiss)}>
					<XMarkIcon aria-hidden="true" />
				</button>
			</div>
		</section>
	);
}
